<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <div id="page-header" class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-green-800">API Keys</h2>
          <p class="mt-2 text-gray-600 text-sm">Manage your API keys and access tokens</p>
        </div>
        <button id="create-key-btn" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
          rounded-md flex items-center
          space-x-2 font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
          focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]"
          @click="showModal = true" v-if="authStore.hasPermissions('manage_api_keys', 'public')">
          <i class="fa-solid fa-key mr-1"></i>
          <span>Generate APIKeys</span>
        </button>
        <Modal :show="showModal" title="Generate APIKeys" size="max-w-4xl" @close="showModal =
          false">
          <form class="space-y-6" @submit.prevent="generateAPIKeys()">
            <div class="space-y-2 mb-6">
              <label class="block text-sm font-medium text-gray-600">Select Environment</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                  <i class="fa-solid fa-flask text-green-600 leading-none"></i>
                </div>
                <select id="keyType" class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20
                  rounded-sm
                   border-gray-300
                   text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
                  focus:ring-green-500 focus:border-transparent" v-model="environment" required>
                  <option value="">Choose</option>
                  <option value="Development">Development</option>
                  <option value="Production">Production</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end space-x-2 mt-5">
              <slot name="footer">
                <button type="button" @click="close()" class="px-4 py-1 bg-gray-300 rounded-sm hover:bg-gray-400">
                  Close
                </button>
                <button type="submit" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
          rounded-md flex items-center
          space-x-2 font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
          focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]">
                  <i class="fa-solid fa-save mr-2"></i>
                  Generate
                </button>
              </slot>
            </div>
          </form>
        </Modal>
        <Modal :show="showUpdateModal" title="Update API Key Status" size="max-w-4xl" @close="showUpdateModal = false">
          <form class="space-y-6" @submit.prevent="updateApiKeyStatus()">
            <div class="space-y-2 mb-6">
              <label class="block text-sm font-medium text-gray-600">Select Status</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                  <i class="fa-solid fa-toggle-on text-green-600 leading-none"></i>
                </div>
                <select id="status" class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20
                  rounded-sm border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
                  focus:ring-green-500 focus:border-transparent" v-model="selectedStatus" required>
                  <option value="">Choose Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end space-x-2 mt-5">
              <button type="button" @click="showUpdateModal = false" class="px-4 py-1 bg-gray-300 rounded-sm hover:bg-gray-400">
                Close
              </button>
              <button type="submit" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
                rounded-md flex items-center space-x-2 font-medium text-white bg-gradient-to-r from-green-700
                to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
                focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]">
                <i class="fa-solid fa-save mr-2"></i>
                Update
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
    <div id="stats-section" class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="p-3 bg-green-100 rounded-lg">
            <i class="fa-solid fa-key text-green-600 text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">Active Keys</p>
            <p class="text-2xl font-semibold text-gray-900">{{ apiKeyStore.activekeysCount }}</p>
          </div>
        </div>
      </div>
      <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="p-3 bg-blue-100 rounded-lg">
            <i class="fa-solid fa-chart-line text-blue-600 text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">Monthly Requests</p>
            <p class="text-2xl font-semibold text-gray-900">{{ apiKeyStore.monthlyUsageCount }}</p>
          </div>
        </div>
      </div>
    </div>
    <div id="usage-guidelines" class="mt-8 bg-green-50 border border-green-200 rounded-lg p-6 mb-4" v-if="success">
      <div class="flex items-start">
        <div class="p-2 bg-green-100 rounded-lg mr-4">
          <i class="fa-solid fa-check text-green-600"></i>
        </div>
        <div>
          <h4 class="text-lg font-semibold text-green-900 mb-2">API Keys generated successfully</h4>
          <div class="text-sm text-green-800 space-y-1">
            <p class="font-semibold">Key:
              {{ apiKey }}
              <button class="ml-2 text-green-400 hover:text-green-600" @click="copyToClipboard(apiKey)">
                <i class="fa-solid fa-copy"></i>
              </button>
            </p>
            <p class="font-semibold">Secret {{ apiSecret }}<button class="ml-2 text-green-400
              hover:text-green-600" @click="copyToClipboard(apiSecret)">
                <i class="fa-solid fa-copy"></i>
              </button></p>
            <p class="mt-8">Please copy these keys and store them in a safe place. Please note
              that the secret key is visible only once</p>
          </div>
        </div>
      </div>
    </div>
    <div id="api-keys-table" class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div v-if="apiKeyStore.loading" class="flex justify-center items-center h-32">
        <lottie-animation path="assets/images/loading.json" :width="150" :height="150" />
      </div>
      <div class="overflow-x-auto" v-if="!apiKeyStore.loading">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" v-if="authStore.hasPermissions('manage_api_keys', 'local')">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr id="api-key-1" v-for="keys in apiKeyStore.apiKeys" :key="keys.id">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="p-2 bg-indigo-100 rounded-lg mr-3">
                    <i class="fa-solid fa-globe text-indigo-600" v-if="keys.credentialEnv.String ==
                      'Production'" />
                    <i class="fa-solid fa-flask text-yellow-600" v-if="keys.credentialEnv.String ==
                      'Development'" />
                  </div>
                  <div>
                    <div class="text-sm font-medium text-gray-900" v-if="keys.credentialEnv.String ==
                      'Production'">Production API</div>
                    <div class="text-sm font-medium text-gray-900" v-if="keys.credentialEnv.String ==
                      'Development'">Development API</div>
                    <div class="text-sm text-gray-500" v-if="keys.credentialEnv.String ==
                      'Production'">Main API production key</div>
                    <div class="text-sm text-gray-500" v-if="keys.credentialEnv.String ==
                      'Development'">SandBox development key</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ keys.organizationName }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <code class="text-sm bg-gray-100 px-2 py-1 rounded">{{ keys.apiKey }}...</code>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100
                  text-green-800" v-if="keys.status == 'ACTIVE'">ACTIVE</span>
                <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100
                  text-red-800" v-if="keys.status != 'ACTIVE'">{{ keys.status.toUpperCase() }}</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(keys.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex space-x-2" v-if="authStore.hasPermissions('manage_api_keys', 'local')">
                  <button class="text-indigo-600 hover:text-indigo-900" @click="openUpdateModal(keys)">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing <span class="font-medium">{{ apiKeyStore.pagination.currentPage }}</span> of <span
                class="font-medium">{{ apiKeyStore.pagination.totalPages }}</span> results
            </div>
            <div class="flex items-center space-x-2" v-if="apiKeyStore.pagination.nextPage > 0">
              <button class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-500 hover:bg-gray-50"
                disabled>Previous</button>
              <button class="px-3 py-1 bg-blue-600 text-white rounded text-sm">{{
                apiKeyStore.pagination.nextPage }}</button>
              <button
                class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="usage-guidelines" class="mt-8 bg-green-50 border border-green-200 rounded-lg p-6" v-if="isPublicPurpose">
      <div class="flex items-start">
        <div class="p-2 bg-green-100 rounded-lg mr-4">
          <i class="fa-solid fa-info-circle text-blue-600"></i>
        </div>
        <div>
          <h4 class="text-lg font-semibold text-green-900 mb-2">API Key Security Guidelines</h4>
          <ul class="text-sm text-green-800 space-y-1">
            <li>• Keep your API keys secure and never share them publicly</li>
            <li>• Use different keys for production and development environments</li>
            <li>• Regularly rotate your API keys for enhanced security</li>
            <li>• Monitor usage patterns to detect unauthorized access</li>
            <li>• Set up proper rate limiting and access controls</li>
          </ul>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useAPIKeysStore } from '@/store/apikeys';
import { useNotificationStore } from '@/store/notifications';
import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue';
import { useAuthStore } from '@/store/auth';
import Header from '../components/header.vue';
import SideBar from '../components/sidebar.vue';
import Modal from '../components/modal.vue';

const apiKeyStore = useAPIKeysStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();

const showModal = ref(false);
const showUpdateModal = ref(false);
const selectedKey = ref<any>(null);
const selectedStatus = ref('');
const environment = ref('');
const loading = ref(false);
const apiKey = ref<string>('');
const apiSecret = ref<string>('');
const success = ref(false);


onMounted(() => {
  const purpose = localStorage.getItem('purpose');
  if (purpose === 'public') {
    apiKeyStore.getAPIKeys(1);
    apiKeyStore.getAPIKeyStats();
  } else if (purpose === 'local') {
    apiKeyStore.getAdminAPIKeys(1);
    apiKeyStore.getAdminAPIKeyStats();
  }
});

const isPublicPurpose = computed(() => {
  const purpose = localStorage.getItem('purpose');
  return purpose === 'public';
});

function formatDate(input: string) {
  const date = new Date(input);
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
  return formatted;
}

function close() {
  showModal.value = false;
}

async function generateAPIKeys() {
  if (!environment.value) {
    notificationStore.showError('Validation Error', 'Please select an environment.');
    return;
  }

  loading.value = true;
  try {
    await apiKeyStore.generateAPIKeys(environment.value);

    if (apiKeyStore.error) {
      notificationStore.showError('API Key Generation Failed', apiKeyStore.error);
    } else {
      notificationStore.showSuccess('API Keys Generated', 'Your API keys have been generated successfully.');
      showModal.value = false;
      success.value = true;
      apiKey.value = apiKeyStore.apiKey || '';
      apiSecret.value = apiKeyStore.apiSecret || '';
      apiKeyStore.getAPIKeys(1);
    }
  } catch (err) {
    notificationStore.showError('API Key Error', 'An unexpected error occurred while generating API keys.');
  } finally {
    loading.value = false;
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    notificationStore.showSuccess('Copied to Clipboard', 'Text has been copied to your clipboard.');
    return true;
  } catch (err) {
    notificationStore.showError('Copy Failed', 'Failed to copy text to clipboard.');
    return false;
  }
}

function openUpdateModal(key: any) {
  selectedKey.value = key;
  selectedStatus.value = key.status;
  showUpdateModal.value = true;
}

async function updateApiKeyStatus() {
  if (!selectedStatus.value) {
    notificationStore.showError('Validation Error', 'Please select a status.');
    return;
  }

  loading.value = true;
  try {
    await apiKeyStore.updateAPIKeyStatus(selectedKey.value.id, selectedStatus.value);

    if (apiKeyStore.error) {
      notificationStore.showError('Update Failed', apiKeyStore.error);
    } else {
      notificationStore.showSuccess('Status Updated', 'API key status has been updated successfully.');
      showUpdateModal.value = false;
      await apiKeyStore.getAPIKeys(1);
    }
  } catch (err) {
    notificationStore.showError('Update Error', 'An unexpected error occurred while updating the API key.');
  } finally {
    loading.value = false;
  }
}

async function deleteApiKey(key: any) {
  if (!confirm('Are you sure you want to delete this API key?')) return;

  loading.value = true;
  try {
    await apiKeyStore.deleteAPIKey(key.id);

    if (apiKeyStore.error) {
      notificationStore.showError('Delete Failed', apiKeyStore.error);
    } else {
      notificationStore.showSuccess('API Key Deleted', 'The API key has been deleted successfully.');
      apiKeyStore.getAPIKeys(1);
    }
  } catch (err) {
    notificationStore.showError('Delete Error', 'An unexpected error occurred while deleting the API key.');
  } finally {
    loading.value = false;
  }
}

</script>
